/**
 * @menciones del hilo de comentarios. Todo puro: el parseo del texto es lo que
 * decide a quién se le notifica, así que se testea sin montar un textarea.
 *
 * El contrato guarda `mentions` como **ids de usuario**, no como nombres: si se
 * guardaran los nombres, renombrar a alguien rompería las menciones viejas.
 */
import type { UserRef } from '@ops/contracts'

/** Un `@algo` a medio escribir en la posición del cursor. */
export interface TokenMencion {
  /** Índice del `@`. */
  desde: number
  /** Lo tipeado después del `@`. */
  consulta: string
}

const TOKEN = /@([^\s@]*)$/

export function tokenMencionEn(texto: string, cursor: number): TokenMencion | null {
  const antes = texto.slice(0, cursor)
  const encontrado = TOKEN.exec(antes)
  if (encontrado === null) return null
  const consulta = encontrado[1] ?? ''
  return { desde: antes.length - consulta.length - 1, consulta }
}

function normalizar(texto: string): string {
  return texto
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
}

/** Candidatos para un `@consulta`. Con la consulta vacía devuelve a todos. */
export function candidatosMencion(
  usuarios: readonly UserRef[],
  consulta: string,
): UserRef[] {
  const q = normalizar(consulta)
  if (q === '') return [...usuarios]
  return usuarios.filter((usuario) => normalizar(usuario.nombre).includes(q))
}

/** Reemplaza el token en curso por el nombre completo y deja el cursor después. */
export function insertarMencion(
  texto: string,
  cursor: number,
  nombre: string,
): { texto: string; cursor: number } {
  const token = tokenMencionEn(texto, cursor)
  if (token === null) return { texto, cursor }
  const antes = `${texto.slice(0, token.desde)}@${nombre} `
  return { texto: antes + texto.slice(cursor), cursor: antes.length }
}

/**
 * Ids de los usuarios mencionados en el cuerpo. Solo cuentan los nombres
 * completos que existen en el directorio: un `@` seguido de cualquier cosa no
 * notifica a nadie, y notificar por coincidencia parcial mandaría avisos al
 * homónimo equivocado.
 */
export function mencionesEn(
  cuerpo: string,
  usuarios: readonly UserRef[],
): string[] {
  const ids = usuarios
    .filter((usuario) => cuerpo.includes(`@${usuario.nombre}`))
    .map((usuario) => usuario.id)
  return [...new Set(ids)]
}

export interface TrozoComentario {
  texto: string
  mencion: boolean
}

/**
 * Parte el cuerpo en trozos de texto y menciones, para poder resaltar las
 * menciones sin meter HTML en un string.
 */
export function partirPorMenciones(
  cuerpo: string,
  usuarios: readonly UserRef[],
): TrozoComentario[] {
  const nombres = usuarios
    .map((usuario) => usuario.nombre)
    // Los más largos primero: "Ana María" antes que "Ana", si no la primera
    // coincidencia parte el nombre por la mitad.
    .sort((a, b) => b.length - a.length)

  const trozos: TrozoComentario[] = []
  let resto = cuerpo

  while (resto !== '') {
    const encontrado = nombres
      .map((nombre) => ({ nombre, indice: resto.indexOf(`@${nombre}`) }))
      .filter((candidato) => candidato.indice !== -1)
      .sort((a, b) => a.indice - b.indice)[0]

    if (encontrado === undefined) {
      trozos.push({ texto: resto, mencion: false })
      break
    }
    if (encontrado.indice > 0) {
      trozos.push({ texto: resto.slice(0, encontrado.indice), mencion: false })
    }
    trozos.push({ texto: `@${encontrado.nombre}`, mencion: true })
    resto = resto.slice(encontrado.indice + encontrado.nombre.length + 1)
  }

  return trozos.filter((trozo) => trozo.texto !== '')
}
